import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Ship, Loader2, Anchor } from "lucide-react";
import { toast } from "sonner";
import { startGoogleLogin } from "@/lib/googleAuth";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/login", { email, password });
      loginWithToken(data.token, data.user);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    startGoogleLogin();
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 wave-bg">
      <div className="absolute top-8 left-8">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-10 w-10 bg-orange border-2 border-ink rounded-2xl flex items-center justify-center shadow-nb-sm">
            <Ship className="h-5 w-5 text-white" />
          </div>
          <span className="font-display font-700 text-2xl tracking-tight">Tugboat</span>
        </Link>
      </div>

      <div className="max-w-md w-full nb-card p-8">
        <div className="inline-flex items-center gap-2 bg-sunny border-2 border-ink rounded-full px-3 py-1 text-xs font-800 uppercase tracking-wide mb-5">
          <Anchor className="h-3.5 w-3.5" /> Welcome back, captain
        </div>
        <h1 className="font-display text-4xl font-700 tracking-tight mb-2">Ahoy! Sign in</h1>
        <p className="text-ink/60 mb-7">Steer back to your feedback console.</p>

        <button
          data-testid="google-login-button"
          onClick={handleGoogle}
          className="nb-btn w-full bg-white py-3 text-sm mb-5"
        >
          <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" />
          Continue with Google
        </button>

        <div className="flex items-center gap-4 mb-5">
          <div className="h-0.5 flex-1 bg-ink/15 rounded-full" />
          <span className="text-xs font-800 text-ink/40 uppercase tracking-widest">or</span>
          <div className="h-0.5 flex-1 bg-ink/15 rounded-full" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label-overline block mb-1.5">Email</label>
            <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="nb-input" placeholder="you@company.com" />
          </div>
          <div>
            <label className="label-overline block mb-1.5">Password</label>
            <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="nb-input" placeholder="••••••••" />
            <div className="text-right mt-1.5">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-700 text-ink/50 hover:text-orange">Forgot password?</Link>
            </div>
          </div>
          <button data-testid="login-submit-button" type="submit" disabled={loading}
            className="nb-btn w-full bg-orange text-white py-3.5 text-sm disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </button>
        </form>

        <p className="text-sm text-ink/60 mt-6 text-center">
          New to Tugboat?{" "}
          <Link to="/register" className="text-orange font-800 hover:underline" data-testid="goto-register-link">Hop aboard</Link>
        </p>
      </div>
    </div>
  );
}
